import { Box, CircularProgress, SxProps, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useAtomValue } from 'jotai';
import { AlgoliaHit, SearchResponse } from 'instantsearch.js';
import { Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { productFamilyMap } from '@kheops/utils';
import { ProductHit } from '../../search/search';
import SizedImage from '../../common/components/SizedImage';
import { useSearchClient } from '../../hooks/useSearchClient';
import useCurrencyFormat from '../../hooks/useCurrencyFormat';
import { currentContextAtom } from '../../state';
import { getProductUrlFromProductHit } from '../discovery.utils';
import FileUtils from '../../common/utils/file.utils';
import { ellipsisStyle, linkStyle } from '../../common/utils/style.utils';
import SearchUtils from '../../common/utils/search.utils';
import WishlistProductButton from '../../wishlist/WishlistProductButton';

interface DiscoverSupplierPackagingSorting {
  packagingsInUserScope: AlgoliaHit<ProductHit>[];
  packagingsWithGMVRank: AlgoliaHit<ProductHit>[];
  packagingsToSortAlphabetically: AlgoliaHit<ProductHit>[];
}

export interface DiscoveryDialogPackagingsProps {
  companyId: string;
  variant?: 'dialog' | 'wishlist';
  sx?: SxProps;
  displayWishlistButton?: boolean;
}

const WISHLIST_PRODUCT_IMAGE_SIZE = 88;

export default function DiscoveryDialogPackagings({
  companyId,
  variant = 'dialog',
  sx,
  displayWishlistButton = false,
}: DiscoveryDialogPackagingsProps): React.JSX.Element | null {
  const { t } = useTranslation(['common', 'wishlist']);
  const searchClient = useSearchClient();
  const currencyFormat = useCurrencyFormat();
  const [isFetchingPackagings, setIsFetchingPackagings] = useState(false);
  const [relevantPackagings, setRelevantPackagings] = useState<AlgoliaHit<ProductHit>[]>([]);
  const { productFamilies } = useAtomValue(currentContextAtom);
  const isWishlistVariant = variant === 'wishlist';

  useEffect((): void => {
    const getRelevantPackagings = async (): Promise<void> => {
      setIsFetchingPackagings(true);
      const { results } = await searchClient.search<AlgoliaHit<ProductHit>>([{ indexName: import.meta.env.REACT_APP_ALGOLIA_PACKAGING_INDEX, params: { filters: `company_id:${companyId}` } }]);
      const unsortedHits = (results[0] as SearchResponse<AlgoliaHit<ProductHit>>).hits;
      let userProductSubFamilies: Product_Sub_Family_Name_Enum[] | undefined;

      if (productFamilies?.length && !productFamilies[0].global_setting) {
        userProductSubFamilies = productFamilies?.flatMap((userProductFamily) => {
          if (userProductFamily.product_sub_family) {
            return userProductFamily.product_sub_family;
          }

          return productFamilyMap[userProductFamily.product_family!];
        });
      }

      const sortedCategories = unsortedHits.reduce<DiscoverSupplierPackagingSorting>((acc, hit) => {
        if (userProductSubFamilies?.includes(hit.family)) {
          acc.packagingsInUserScope.push(hit);
        } else if (hit.customGMVRank) {
          acc.packagingsWithGMVRank.push(hit);
        } else {
          acc.packagingsToSortAlphabetically.push(hit);
        }

        return acc;
      }, { packagingsInUserScope: [], packagingsWithGMVRank: [], packagingsToSortAlphabetically: [] } as DiscoverSupplierPackagingSorting);

      sortedCategories.packagingsToSortAlphabetically = sortedCategories.packagingsToSortAlphabetically.sort((packagingA, packagingB) => packagingA.name.localeCompare(packagingB.name));

      const sortedPackagings = [
        sortedCategories.packagingsInUserScope,
        sortedCategories.packagingsWithGMVRank,
        sortedCategories.packagingsToSortAlphabetically,
      ].flat().slice(0, 3);

      setRelevantPackagings(sortedPackagings);
      setIsFetchingPackagings(false);
    };

    getRelevantPackagings();
  }, [productFamilies]);

  if (!isFetchingPackagings && !relevantPackagings.length) {
    return null;
  }
 
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {!isWishlistVariant && !isFetchingPackagings && (
        <Typography variant="titleSmall">
          {t('contracts:supplier_best_sellers')}
        </Typography>
      )}
      {/* eslint-disable-next-line no-nested-ternary */}
      {isFetchingPackagings
        ? <CircularProgress sx={{ mx: 'auto', my: 3 }} />
        : (
          isWishlistVariant
          ? (
            <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 1, minWidth: 0, pt: 1 }}>
              <Typography variant="bodySmall" color="onSurfaceVariant">
                {t('wishlist:top_products')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {relevantPackagings.map((hit) => (
                <Link
                  key={hit.objectID}
                  to={getProductUrlFromProductHit(hit)}
                  state={{ origin: 'DISCOVERY' }}
                  style={{ textDecoration: 'none' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      borderRadius: 3,
                      bgcolor: 'surfaceContainerLow.main',
                      ...linkStyle,
                    }}
                  >
                    <Box
                      component="img"
                      src={FileUtils.BuildPackagingImageUrl({
                        photo: hit.photo,
                        type: hit.family,
                        size: {
                          width: WISHLIST_PRODUCT_IMAGE_SIZE,
                          height: WISHLIST_PRODUCT_IMAGE_SIZE,
                          fit: 'contain',
                        },
                      })}
                      alt={t('common:product_one')}
                      sx={{
                        width: WISHLIST_PRODUCT_IMAGE_SIZE,
                        height: WISHLIST_PRODUCT_IMAGE_SIZE,
                        objectFit: 'contain',
                      }}
                    />
                    <Typography variant="titleSmall">
                      {SearchUtils.getFormatedUnitPriceFromAlgoliaHit(hit, currencyFormat.format)}
                    </Typography>
                  </Box>
                </Link>
              ))}
              </Box>
            </Box>
          ) : (
          <Box
            sx={{              
              display: 'flex',
              flexShrink: 0,
              overflowX: { xs: 'auto', lg: 'unset' },
              padding: { xs: 2, lg: 0 },
              margin: { xs: -2, lg: 0 },
              gap: 2,
              ...sx,
            }}
          >
            {relevantPackagings.map((hit) => (
              <Box
                key={hit.objectID}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  flexShrink: 0,
                  py: 1,
                  px: 1.5,
                  borderRadius: 3,
                  boxShadow: '0 4px 15px 0 rgba(0, 0, 0, 0.03), 0 4px 15px 0 rgba(0, 0, 0, 0.09)',
                  width: 145,
                  height: 148,
                }}
              >
                <Box
                  component={Link}
                  to={getProductUrlFromProductHit(hit)}
                  state={{ origin: 'DISCOVERY' }}
                  sx={linkStyle}
                >
                  <SizedImage
                    src={FileUtils.BuildPackagingImageUrl({ photo: hit.photo, type: hit.family, size: { width: 97, height: 96, fit: 'contain' } })}
                    alt={t('common:product_one')}
                    height={96}
                    width={97}
                    sx={{
                      aspectRatio: '97/96',
                      borderRadius: 0,
                      mb: 1,
                      marginX: 'auto',
                    }}
                  />
                  <Typography variant="bodySmall" sx={{ ...ellipsisStyle, mb:0.5 }}>
                    {hit.name}
                  </Typography>
                  <Typography variant="titleSmall">
                    {SearchUtils.getFormatedUnitPriceFromAlgoliaHit(hit, currencyFormat.format)}
                  </Typography>
                </Box>
                {displayWishlistButton && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 48,
                      right: 12.5,
                      zIndex: 5,
                    }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <WishlistProductButton
                      supplierId={companyId}
                      productSku={Number(hit.productSku)}
                      variant="small"
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        ))}
    </Box>
  );
}
